import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../client';
import { qk } from './keys';
import type { PaymentIntent, Subscription, SubscriptionPlan } from '../types';

export function useSubscriptionPlans(activeOnly = true) {
  return useQuery({
    queryKey: [...qk.subscriptionPlans, activeOnly] as const,
    // Addendum-01 Q2: plans come from backend (admin CRUD).
    queryFn: () => apiGet<SubscriptionPlan[]>(`/subscriptions/plans?active=${activeOnly}`),
    staleTime: 60_000,
  });
}

export function useMySubscription() {
  return useQuery({
    queryKey: qk.subscriptionMe,
    queryFn: async () => {
      try {
        return await apiGet<Subscription>('/subscriptions/me');
      } catch {
        return null;
      }
    },
  });
}

export function useSubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planCode, paymentMethodId }: { planCode: string; paymentMethodId?: string }) =>
      apiPost<PaymentIntent>('/subscriptions/subscribe', {
        plan_code: planCode,
        payment_method_id: paymentMethodId ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.subscriptionMe });
    },
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<Subscription>('/subscriptions/cancel'),
    onSuccess: (data) => {
      qc.setQueryData(qk.subscriptionMe, data);
    },
  });
}

export function useResumeSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<Subscription>('/subscriptions/resume'),
    onSuccess: (data) => {
      qc.setQueryData(qk.subscriptionMe, data);
    },
  });
}
